import React from 'react';
import { Link } from 'react-router-dom';
import { Calendar, TrendingUp, Zap } from 'lucide-react';
import { Button } from '../components/ui/button';
import Header from '../components/Header';
import Footer from '../components/Footer';

const InsightsPage = () => {
  const articles = [
    {
      category: "Pricing Strategy",
      title: "5 Pricing Mistakes That Cost SMBs Thousands",
      excerpt: "Discover the most common pricing errors small businesses make and how to avoid them with intelligent automation.",
      date: "January 15, 2025",
      readTime: "8 min read",
      image: "https://images.pexels.com/photos/5716001/pexels-photo-5716001.jpeg",
      slug: "5-pricing-mistakes-cost-smbs-thousands"
    },
    {
      category: "AI & Technology",
      title: "How AI is Democratizing Enterprise Pricing",
      excerpt: "Machine learning technology that was once only available to large enterprises is now accessible to businesses of all sizes.",
      date: "January 10, 2025",
      readTime: "6 min read",
      image: "https://images.pexels.com/photos/17485658/pexels-photo-17485658.png",
      slug: "ai-democratizing-enterprise-pricing"
    },
    {
      category: "Case Study",
      title: "How a Retail SMB Increased Margins by 28%",
      excerpt: "Real world success story: A mid market retailer's journey from spreadsheets to AI powered pricing intelligence.",
      date: "January 5, 2025",
      readTime: "10 min read",
      image: "https://images.pexels.com/photos/3183197/pexels-photo-3183197.jpeg",
      slug: "retail-smb-increased-margins-28-percent"
    },
    {
      category: "Industry Trends",
      title: "2025 Pricing Trends Every SMB Should Know",
      excerpt: "From dynamic pricing to value based models, explore the pricing strategies shaping the future of business.",
      date: "December 28, 2024",
      readTime: "7 min read",
      image: "https://images.unsplash.com/photo-1760239037245-a372db8630f3",
      slug: "2025-pricing-trends-smb-should-know"
    },
    {
      category: "Best Practices",
      title: "Building a Data Driven Pricing Culture",
      excerpt: "Transform your team's approach to pricing with these proven strategies and workflows.",
      date: "December 20, 2024",
      readTime: "9 min read",
      image: "https://images.pexels.com/photos/5716037/pexels-photo-5716037.jpeg",
      slug: "building-data-driven-pricing-culture"
    },
    {
      category: "Product Updates",
      title: "Introducing Real-Time Competitive Intelligence",
      excerpt: "Our latest feature helps you monitor competitor pricing and automatically adjust your strategy.",
      date: "December 15, 2024",
      readTime: "5 min read",
      image: "https://images.pexels.com/photos/17484975/pexels-photo-17484975.png",
      slug: "real-time-competitive-intelligence"
    }
  ];

  return (
    <div className="min-h-screen bg-[#0A0F1C]">
      <Header />
      
      {/* Hero */}
      <section className="pt-40 pb-20 px-6 bg-gradient-to-b from-[#0A0F1C] to-[#111827]">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-6xl md:text-7xl font-bold text-[#F8FAFC] mb-6">
            Pricing
            <span className="bg-gradient-to-r from-[#2563EB] to-[#3B82F6] bg-clip-text text-transparent"> Insights</span>
          </h1>
          <p className="text-xl text-[#94A3B8] max-w-3xl mx-auto">
            Expert perspectives, industry trends, and actionable strategies to help SMBs master pricing.
          </p>
        </div>
      </section>

      {/* Articles Grid */}
      <section className="py-24 px-6 bg-[#0A0F1C]">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {articles.map((article, index) => (
              <Link 
                key={index} 
                to={`/insights/${article.slug}`}
                className="bg-gradient-to-br from-[#111827] to-[#0A0F1C] border border-[#2563EB]/30 rounded-2xl overflow-hidden hover:border-[#2563EB]/60 transition-all hover:shadow-xl hover:shadow-[#2563EB]/20 cursor-pointer group block"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={article.image}
                    alt={article.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A0F1C] to-transparent" />
                  <div className="absolute top-4 left-4">
                    <span className="bg-[#2563EB] text-white px-3 py-1 rounded-full text-sm font-semibold">
                      {article.category}
                    </span>
                  </div>
                </div>
                
                <div className="p-6">
                  <h3 className="text-2xl font-bold text-[#F8FAFC] mb-3 group-hover:text-[#2563EB] transition-colors">
                    {article.title}
                  </h3>
                  <p className="text-[#94A3B8] mb-4 line-clamp-3">{article.excerpt}</p>
                  
                  <div className="flex items-center justify-between text-sm text-[#94A3B8]">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      {article.date}
                    </div>
                    <span>{article.readTime}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter */}
      <section className="py-24 px-6 bg-gradient-to-b from-[#0A0F1C] to-[#111827]">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-gradient-to-br from-[#111827] to-[#0A0F1C] border border-[#2563EB]/30 rounded-2xl p-12">
            <h2 className="text-4xl font-bold text-[#F8FAFC] mb-4">
              Get Pricing Insights Delivered
            </h2>
            <p className="text-xl text-[#94A3B8] mb-8">
              Join 5,000+ SMB leaders receiving weekly pricing strategies and industry analysis.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 max-w-xl mx-auto">
              <input
                type="email"
                placeholder="Enter your email"
                className="flex-1 px-6 py-4 bg-[#0A0F1C] border border-[#2563EB]/30 rounded-lg text-[#F8FAFC] focus:outline-none focus:border-[#2563EB]"
              />
              <Button className="bg-[#2563EB] hover:bg-[#1d4ed8] text-white px-8 py-4 rounded-lg font-semibold">
                Subscribe
              </Button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default InsightsPage;